import { CSSObjectWithLabel } from 'react-select';

import { ColDef } from 'ag-grid-community';

import { FormattingUtils } from '@/utils/Formatting.utils';
import { PreloadData } from '@/utils/microfrontend/preloadData';
import { VarBillingUsageDetailOutput, VarBillingUsageDetailStatusEnum, VarBillingUsageHeaderStatusEnum } from
  '@/graphql/contracts/_generated_/contracts';
import { NumberUtils } from '@/utils/numbers.utils';
import {
  ignoreCommentDynamicColumn,
  predictedNetValueDynamicColumn, priceCurrencyDynamicColumns, pricingModelDynamicColumn,
  statusMessageDynamicColumn
} from '../UsageDetails/columns';

export const hideSalerOrderBtnTooltip = (headerStatus?: VarBillingUsageHeaderStatusEnum | null) => {
  return headerStatus === VarBillingUsageHeaderStatusEnum.Matched ||
    headerStatus === VarBillingUsageHeaderStatusEnum.SalesOrdersGenerated ||
    headerStatus === VarBillingUsageHeaderStatusEnum.SalesOrdersGeneration ||
    headerStatus === VarBillingUsageHeaderStatusEnum.SalesOrdersGenerationError ||
    headerStatus === VarBillingUsageHeaderStatusEnum.PartiallyProcessed;
};

export const salesOrderGenerationSuccessStatuses = [
  VarBillingUsageHeaderStatusEnum.SalesOrdersGeneration,
  VarBillingUsageHeaderStatusEnum.SalesOrdersGenerated,
  VarBillingUsageHeaderStatusEnum.PartiallyProcessed
];

export const abandonedStatuses = [VarBillingUsageHeaderStatusEnum.Abandoned];

export const disabledHeaderInputStatuses = [
  VarBillingUsageHeaderStatusEnum.SalesOrdersGeneration,
  VarBillingUsageHeaderStatusEnum.SalesOrdersGenerated,
  VarBillingUsageHeaderStatusEnum.PartiallyProcessed,
  ...abandonedStatuses
];

export const disabledMatchActionStatuses = [
  ...salesOrderGenerationSuccessStatuses,
  ...abandonedStatuses,
  VarBillingUsageHeaderStatusEnum.Matching
];

export const errorStatuses = [VarBillingUsageDetailStatusEnum.MatchingError,
  VarBillingUsageDetailStatusEnum.SalesOrdersGenerationError];

export const matchingTriggeredStatuses = [
  VarBillingUsageHeaderStatusEnum.Matched,
  VarBillingUsageHeaderStatusEnum.Matching,
  VarBillingUsageDetailStatusEnum.MatchingError,
  VarBillingUsageDetailStatusEnum.SalesOrdersGenerationError,
  ...salesOrderGenerationSuccessStatuses
];

export const restoreHeaderStatuses = [
  VarBillingUsageHeaderStatusEnum.Draft, 
  VarBillingUsageHeaderStatusEnum.Imported, 
  VarBillingUsageHeaderStatusEnum.MatchingError, 
  VarBillingUsageHeaderStatusEnum.Matched,
  VarBillingUsageHeaderStatusEnum.SalesOrdersGenerationError

];

export const STATUS_COLUMN = 'status';
export const NET_COLUMN = 'net';
export const PRICE_COLUMN = 'price';

export const getDynamicColumns = (
  columns: ColDef[],
  statusMessageIsPresent: VarBillingUsageDetailOutput | undefined,
  matchingWasTrigged: boolean,
  ignoreCommentIsPresent?: VarBillingUsageDetailOutput | undefined
): ColDef<VarBillingUsageDetailOutput>[] => {
  return columns.reduce((colDefs: ColDef[], col: ColDef) => {
    if (col.field === STATUS_COLUMN) {
      const dynamicCols = [...colDefs, col];
      if (statusMessageIsPresent) {
        dynamicCols.push(statusMessageDynamicColumn);
      }
      if (ignoreCommentIsPresent) {
        dynamicCols.push(ignoreCommentDynamicColumn);
      }
      return dynamicCols;
    }
    if (col.field === NET_COLUMN && matchingWasTrigged) {
      return [...colDefs, col, predictedNetValueDynamicColumn, ...priceCurrencyDynamicColumns];
    }

    if (col.field === PRICE_COLUMN && matchingWasTrigged) {
      return [...colDefs, col, pricingModelDynamicColumn];
    }

    return [...colDefs, col];

  }, []);
};


export const mapEnumToFormattedStatus = (value?: VarBillingUsageHeaderStatusEnum | null) => {
  const statuses = PreloadData.usageHeaderStatuses();
  return statuses.find(usageDetailStatus => usageDetailStatus.status === value)?.formattedStatus ?? value;
};

export const encodePaginationPageToBase64 = (page: number) => {
  const byteArray = Array(3).fill(0);
  const pageNumberBase64Encoded = btoa(String.fromCharCode(...new Uint8Array([page - 1, ...byteArray])));

  return pageNumberBase64Encoded;
};

export const mapEnumToFormattedUsageDetailsStatus = (value: VarBillingUsageDetailStatusEnum) => {
  const statuses = PreloadData.varBillingUsageDetailsStatuses();
  return statuses.find(usageDetailStatus => usageDetailStatus.status === value)?.formattedStatus ?? value;
};

export const getRowsImportedText = (totalCount?: number) => {
  return `${totalCount ?? 0} ${FormattingUtils.pluralize(
    totalCount ?? 0, 'row')} imported`;
};

export const getRowsMatchedText = (matchedCount?: number) => {
  return `${matchedCount ?? 0} ${FormattingUtils.pluralize(
    matchedCount ?? 0, 'row')} matched`;
};

export const getTotalValueImported = (totalNet?: string | null) => {
  return `Total value imported: ${NumberUtils.prettifyWithRound(totalNet)}`;
};

export const getTotalValueMatchedCalculated = (calculatedNet?: string | null) => {
  return `Calculated total value: ${NumberUtils.prettifyWithRound(calculatedNet)}`;
};

export function selectOrNullify<T>(oldValue: T, newValue: T) {
  return oldValue === newValue ? null : newValue;
}

export const getdropDownCommonStyling = (disabled: boolean) => ({
  border: disabled ? 'none' : '1px solid #828D98',
  borderRadius: '3px',

  '.ag-dropdown__control': {
    height: '30px',
    alignContent: 'center'
  },

  '&:focus-within': {
    padding: '2px',
    borderRadius: '5px',
    border: '2px solid #0075C9',
  }

}) as CSSObjectWithLabel;
